// Anhänge anfügen (19.10.2026, Wunsch Simon): Büroklammer mit Menü (Vom Rechner, aus den Dateien des Leads, feste Unterlagen wie
// Weiterbildungsübersicht und Präsentation) und Ziehen/Ablegen vom Rechner. Genutzt im E-Mail-Fenster (neu, Antworten,
// Weiterleiten), in der Massen-Mail und im Vorlagen-Editor (feste Anhänge an Vorlagen).
import { useEffect, useRef, useState, type DragEvent } from "react";
import { dateienApi, type LeadDatei } from "../api/dateien";
import { mailEntwuerfeApi, type Unterlage } from "../api/mailEntwuerfe";
import { Ausklapp } from "../components/Ausklapp";
import { Icon } from "../components/Icon";
import { groesseText } from "./logik";
import "./kommunikation.css";

interface Props {
  /** Lead, aus dessen Dateien gewählt werden kann (fehlt bei Massen-Mail und Vorlagen) */
  leadId?: string;
  onRechner: (dateien: File[]) => void;
  onUnterlage?: (u: Unterlage) => void;
  onLeadDatei?: (d: LeadDatei) => void;
  disabled?: boolean;
  /** Schrift neben der Büroklammer */
  beschriftung?: string;
}

export function AnhangKnopf({ leadId, onRechner, onUnterlage, onLeadDatei, disabled, beschriftung = "Anhang" }: Props) {
  const [anker, setAnker] = useState<HTMLButtonElement | null>(null);
  const [offen, setOffen] = useState(false);
  const [ansicht, setAnsicht] = useState<"menue" | "lead">("menue");
  const [unterlagen, setUnterlagen] = useState<Unterlage[] | null>(null);
  const [leadDateien, setLeadDateien] = useState<LeadDatei[] | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const wahl = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!offen || !onUnterlage || unterlagen) return;
    mailEntwuerfeApi.unterlagen().then((r) => setUnterlagen(r.data)).catch(() => setUnterlagen([]));
  }, [offen, onUnterlage, unterlagen]);
  useEffect(() => {
    if (!offen || ansicht !== "lead" || !leadId || leadDateien) return;
    dateienApi
      .liste(leadId)
      .then((r) => setLeadDateien(r.data.filter((d) => !d.eingebettet && d.herunterladbar)))
      .catch(() => setFehler("Die Dateien des Leads ließen sich nicht laden."));
  }, [offen, ansicht, leadId, leadDateien]);

  const zu = () => {
    setOffen(false);
    setAnsicht("menue");
  };

  return (
    <>
      <button
        ref={setAnker}
        type="button"
        className="km-anhangknopf"
        disabled={disabled}
        aria-label="Datei anhängen"
        aria-haspopup="menu"
        aria-expanded={offen}
        title="Datei anhängen (oder Dateien ins Fenster ziehen)"
        onClick={() => (onUnterlage || (leadId && onLeadDatei) ? setOffen((o) => !o) : wahl.current?.click())}
      >
        <Icon name="anhang" groesse={15} />
        {beschriftung ? <span>{beschriftung}</span> : null}
      </button>
      <input
        ref={wahl}
        type="file"
        multiple
        hidden
        onChange={(e) => {
          const gewaehlt = Array.from(e.target.files ?? []);
          e.target.value = ""; // dieselbe Datei lässt sich danach erneut wählen
          onRechner(gewaehlt);
        }}
      />
      <Ausklapp anker={anker} offen={offen} onSchliessen={zu} breite={260} rolle="menu" className="ausklapp--ueber-dialog">
        {ansicht === "menue" ? (
          <div className="menue">
            <button
              type="button"
              className="menue__punkt"
              onClick={() => {
                zu();
                wahl.current?.click();
              }}
            >
              <Icon name="anhang" groesse={15} /> Vom Rechner …
            </button>
            {leadId && onLeadDatei ? (
              <button type="button" className="menue__punkt" onClick={() => setAnsicht("lead")}>
                <Icon name="karte" groesse={15} /> Aus den Dateien des Leads …
              </button>
            ) : null}
            {onUnterlage ? (
              <>
                <div className="menue__ueberschrift">Feste Unterlagen</div>
                {unterlagen === null ? <div className="menue__ueberschrift">Lädt …</div> : null}
                {unterlagen?.length === 0 ? <div className="menue__ueberschrift">Keine hinterlegt</div> : null}
                {(unterlagen ?? []).map((u) => (
                  <button
                    key={u.name}
                    type="button"
                    className="menue__punkt"
                    title={u.dateiname}
                    onClick={() => {
                      zu();
                      onUnterlage(u);
                    }}
                  >
                    <Icon name="notiz" groesse={15} /> {u.titel} <span className="km-leise">{groesseText(u.groesse)}</span>
                  </button>
                ))}
              </>
            ) : null}
          </div>
        ) : (
          <div className="menue">
            <button type="button" className="menue__punkt" onClick={() => setAnsicht("menue")}>
              <Icon name="chevron-links" groesse={15} /> Zurück
            </button>
            <div className="menue__ueberschrift">Dateien des Leads</div>
            {fehler ? <div className="menue__ueberschrift">{fehler}</div> : null}
            {leadDateien === null && !fehler ? <div className="menue__ueberschrift">Lädt …</div> : null}
            {leadDateien?.length === 0 ? <div className="menue__ueberschrift">Keine Dateien am Lead</div> : null}
            {(leadDateien ?? []).map((d) => (
              <button
                key={d.id}
                type="button"
                className="menue__punkt"
                title={d.titel ?? d.name}
                onClick={() => {
                  zu();
                  onLeadDatei?.(d);
                }}
              >
                <span className="km-datei__name">{d.name}</span> {d.size ? <span className="km-leise">{groesseText(d.size)}</span> : null}
              </button>
            ))}
          </div>
        )}
      </Ausklapp>
    </>
  );
}

/** Ziehen und Ablegen vom Rechner: Eigenschaften für den Rahmen, in den Dateien gelegt werden dürfen. */
export function useAblage(onDateien: (dateien: File[]) => void, aus = false) {
  const [ueber, setUeber] = useState(false);
  const tiefe = useRef(0);
  const mitDateien = (e: DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes("Files");
  if (aus) return { ueber: false, props: {} };
  return {
    ueber,
    props: {
      onDragEnter: (e: DragEvent) => {
        if (!mitDateien(e)) return;
        e.preventDefault();
        tiefe.current += 1;
        setUeber(true);
      },
      onDragOver: (e: DragEvent) => {
        if (!mitDateien(e)) return;
        e.preventDefault(); // sonst lässt der Browser kein Ablegen zu
        e.dataTransfer.dropEffect = "copy";
      },
      onDragLeave: (e: DragEvent) => {
        if (!mitDateien(e)) return;
        tiefe.current = Math.max(0, tiefe.current - 1);
        if (tiefe.current === 0) setUeber(false);
      },
      onDrop: (e: DragEvent) => {
        if (!mitDateien(e)) return;
        e.preventDefault();
        tiefe.current = 0;
        setUeber(false);
        const dateien = Array.from(e.dataTransfer.files ?? []);
        if (dateien.length) onDateien(dateien);
      },
    },
  };
}
